// src/services/authService.js
// ─────────────────────────────────────────────────────────────
//  Firebase Authentication: 이메일/비밀번호 + Google 로그인
//  사용자 프로필은 Firestore "users" 컬렉션에 별도 저장
// ─────────────────────────────────────────────────────────────

import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  onAuthStateChanged,
  updateProfile,
  sendPasswordResetEmail,
  deleteUser,
} from "firebase/auth";
import {
  doc, setDoc, getDoc, updateDoc, serverTimestamp,
} from "firebase/firestore";
import { auth, db } from "./firebase";

const USERS_COL = "users";
const googleProvider = new GoogleAuthProvider();

// ── 사용자 Firestore 문서 생성/갱신 ──────────────────────────

async function upsertUserDoc(uid, data) {
  const ref  = doc(db, USERS_COL, uid);
  const snap = await getDoc(ref);
  if (snap.exists()) {
    await updateDoc(ref, { ...data, updatedAt: serverTimestamp() });
  } else {
    await setDoc(ref, {
      ...data,
      role:      data.role ?? "worker",   // worker | employer
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  }
}

// ── 회원가입 ──────────────────────────────────────────────────

/**
 * 이메일/비밀번호 회원가입
 * @param {string} email
 * @param {string} password
 * @param {object} profile  { name, phone, role }
 */
export async function signUp(email, password, profile) {
  const cred = await createUserWithEmailAndPassword(auth, email, password);
  await updateProfile(cred.user, { displayName: profile.name });
  await upsertUserDoc(cred.user.uid, {
    email,
    name:  profile.name,
    phone: profile.phone ?? "",
    role:  profile.role  ?? "worker",
  });
  return cred.user;
}

// ── 로그인 ────────────────────────────────────────────────────

export async function signIn(email, password) {
  const cred = await signInWithEmailAndPassword(auth, email, password);
  return cred.user;
}

export async function signInWithGoogle() {
  const cred = await signInWithPopup(auth, googleProvider);
  await upsertUserDoc(cred.user.uid, {
    email: cred.user.email,
    name:  cred.user.displayName ?? "",
    photo: cred.user.photoURL    ?? "",
  });
  return cred.user;
}

// ── 로그아웃 ──────────────────────────────────────────────────

export async function logOut() {
  await signOut(auth);
}

// ── 비밀번호 재설정 ───────────────────────────────────────────

export async function resetPassword(email) {
  await sendPasswordResetEmail(auth, email);
}

// ── 현재 사용자 Firestore 프로필 조회 ─────────────────────────

export async function getUserProfile(uid) {
  const snap = await getDoc(doc(db, USERS_COL, uid));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
}

export async function updateUserProfile(uid, updates) {
  await updateDoc(doc(db, USERS_COL, uid), {
    ...updates,
    updatedAt: serverTimestamp(),
  });
  if (updates.name && auth.currentUser) {
    await updateProfile(auth.currentUser, { displayName: updates.name });
  }
}

// ── 역할(role) 변경 ───────────────────────────────────────────

export async function switchRole(uid, role) {
  await updateUserProfile(uid, { role });
}

// ── Auth 상태 구독 ────────────────────────────────────────────

/**
 * 앱 최상단에서 한 번 호출
 * @param {function} callback - (user | null) => void
 * @returns unsubscribe
 */
export function onAuthChange(callback) {
  return onAuthStateChanged(auth, callback);
}

// ── 계정 삭제 ─────────────────────────────────────────────────

export async function deleteAccount() {
  if (!auth.currentUser) throw new Error("로그인이 필요합니다.");
  await deleteUser(auth.currentUser);
}
